// The Activities dashboard's selection result (utils/eeActivities.ts EeResponse) built from the data API's rows for
// an area made on the catalogue 1.0 maps (admin_lvl_<n> or model_grid). The sums are the demo API's
// (app-backend-demo/lib/eeActivities.ts figures and summariseAdmin, lib/catalogueAreas.ts): counts and modes are
// summed over the areas, the averages weighted by arrivals, one summary per hour (one for daily). Summed people count
// someone in several areas once in each, so the result says when its people are such a sum. Minutes spent do add up,
// so the busiest hour's people present come from them; the admin tables have no minutes and go without.
//
// What the demo API adds for the model grid, coverage and "include more cells", is not built here: every selected
// cell or area is counted as it is (an area never holds a cell together with its parent or child, so no ground is
// counted twice), coverage is left null and there is nothing to grow. Pure: no React, router or network.
import { gridSizeM } from '../config/catalogue';
import { rowAreaKey } from '../services/catalogueRequests';
import { nullableNumber, unwrap } from '../services/rowValues';
import { buildDistributions, rowHour, toDistributionRow } from './eeDistributions';
import { EE_MODES, EE_ROLES } from './eeActivities';
import type { EeDayArrivals, EeDistribution, EeMode, EeResponse, EeRow, EeSummary } from './eeActivities';

type Num = number | null;
type Grain = 'daily' | 'hourly';

/** Sum that ignores nulls; null when there was nothing to add. */
function nullSum(values: Num[]): Num {
  let sum: Num = null;
  for (const v of values) if (v !== null) sum = (sum ?? 0) + v;
  return sum;
}

/** Average weighted by arrivals over the rows that have both figures; null when no weight is left. */
function weighted(rows: readonly EeRow[], pick: (r: EeRow) => Num): Num {
  let total = 0, weight = 0;
  for (const r of rows) {
    const v = pick(r);
    const w = r.arrivals;
    if (v === null || w === null || !(w > 0)) continue;
    total += v * w;
    weight += w;
  }
  return weight > 0 ? Math.round((total / weight) * 100) / 100 : null;
}

/** The figures of a selection from its counted rows: sums for counts and modes, arrival-weighted averages. */
export function figures(counted: readonly EeRow[]) {
  const modes = {} as Record<EeMode, Num>;
  for (const mode of EE_MODES) modes[mode] = nullSum(counted.map((r) => r[`arrivals_${mode}_modelled`]));
  return {
    people: nullSum(counted.map((r) => r.sum_unique_people)),
    arrivals: nullSum(counted.map((r) => r.arrivals)),
    modes,
    average_distance: weighted(counted, (r) => r.average_distance),
    average_travel_time: weighted(counted, (r) => r.average_travel_time),
    average_visit_length: weighted(counted, (r) => r.average_visit_length),
  };
}

/** One summary per hour (a null hour for daily) over all the rows, with no coverage. */
export function summarise(rows: readonly EeRow[]): EeSummary[] {
  const byHour = new Map<number | null, EeRow[]>();
  for (const r of rows) {
    const list = byHour.get(r.hour_of_day);
    if (list) list.push(r);
    else byHour.set(r.hour_of_day, [r]);
  }
  const hours = Array.from(byHour.keys()).sort((a, b) => (a ?? -1) - (b ?? -1));
  return hours.map((hour) => ({
    hour_of_day: hour,
    ...figures(byHour.get(hour) as EeRow[]),
    coverage: null,
    covered_km2: null,
    uncovered_km2: null,
    uncovered_cells: 0,
  }));
}

/**
 * A data API activities row as a counted row of the result; null for a row with no area id. A grid row's id is
 * keyed with its size, as the area saves its cells, and the cell is its own map cell.
 */
export function toEeRow(raw: unknown, type: Grain): EeRow | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = unwrap(r.geo_id);
  if (id == null || String(id) === '') return null;
  const geoType = unwrap(r.geo_type);
  const key = rowAreaKey(id, geoType);
  const name = unwrap(r.geo_name);
  // The backend marks an unnamed area "no_name", and the grid's names are the cell ids again: both read as no name.
  const geoName = name == null || name === 'no_name' || String(name) === String(id) || String(name) === key ? null : String(name);
  const tier = unwrap(r.k5_tier);
  return {
    geo_id: key,
    geo_name: geoName,
    hour_of_day: rowHour(r, type),
    size_m: typeof geoType === 'string' ? gridSizeM(geoType) : null,
    k5_tier: tier == null ? null : String(tier),
    role: 'counted',
    counted_in: null,
    cells: [key],
    sum_unique_people: nullableNumber(r.sum_unique_people),
    arrivals: nullableNumber(r.arrivals),
    average_distance: nullableNumber(r.average_distance),
    average_travel_time: nullableNumber(r.average_travel_time),
    average_visit_length: nullableNumber(r.average_visit_length),
    arrivals_car_modelled: nullableNumber(r.arrivals_car_modelled),
    arrivals_walk_modelled: nullableNumber(r.arrivals_walk_modelled),
    arrivals_bike_modelled: nullableNumber(r.arrivals_bike_modelled),
    arrivals_transit_modelled: nullableNumber(r.arrivals_transit_modelled),
    arrivals_other_modelled: nullableNumber(r.arrivals_other_modelled),
  };
}

/** Rows by hour, then area id. */
export const byHourThenId = (a: EeRow, b: EeRow) =>
  (a.hour_of_day ?? -1) - (b.hour_of_day ?? -1) || a.geo_id.localeCompare(b.geo_id, undefined, { numeric: true });

/**
 * The day's arrivals per group: everyone from the daily summary, each role from its daily distributions (modelled;
 * roles are not in the activity tables). The measures of a distribution all sum to the same arrivals, so the first
 * one with a figure is used.
 */
export function dayArrivalsOf(everyone: Num, distributions: readonly EeDistribution[]): EeDayArrivals {
  const out = { everyone } as EeDayArrivals;
  for (const role of EE_ROLES) out[role] = distributions.find((d) => d.group === role && d.arrivals !== null)?.arrivals ?? null;
  return out;
}

/** The hour with the most people present in hourly rows, over all their areas; undefined when no row has minutes spent. */
export function busiestTime(raw: readonly unknown[]): { hour: number; people: number } | undefined {
  const minutes = new Map<number, number>();
  for (const row of raw) {
    if (row === null || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const spent = nullableNumber(r.stays_dwell_minutes);
    const hour = rowHour(r, 'hourly');
    if (spent !== null && hour !== null) minutes.set(hour, (minutes.get(hour) ?? 0) + spent);
  }
  let best: { hour: number; people: number } | undefined;
  for (const [hour, spent] of Array.from(minutes)) {
    if (!best || spent / 60 > best.people) best = { hour, people: spent / 60 };
  }
  return best;
}

/** The counted rows of an answer, read and sorted. */
function countedRows(raw: readonly unknown[], type: Grain): EeRow[] {
  return raw.map((r) => toEeRow(r, type)).filter((r): r is EeRow => r !== null).sort(byHourThenId);
}

/** The distributions of exactly the (area, hour) pairs the summary counted. */
function distributionsOf(raw: readonly unknown[], type: Grain, counted: readonly EeRow[]): EeDistribution[] {
  const keep = new Set(counted.map((r) => `${r.geo_id}|${r.hour_of_day ?? ''}`));
  const rows = raw
    .map((r) => toDistributionRow(r, type))
    .filter((r): r is NonNullable<typeof r> => r !== null && keep.has(`${r.geo_id}|${r.hour_of_day ?? ''}`));
  return buildDistributions(rows);
}

/** What the data API sent for one selection: every row of each answer. */
export interface CatalogueActivitiesRows {
  /** The area type the person picked (admin_lvl_<n> or model_grid), not the per-size geo types of the requests. */
  geoType: string;
  /** The delivery day. */
  date: string;
  type: Grain;
  activities: readonly unknown[];
  distributions: readonly unknown[];
  /** Hourly answers only: the same selection asked for daily, for the day's arrivals per group. */
  day?: { activities: readonly unknown[]; distributions: readonly unknown[] };
  /** Daily answers on the grid: the same selection's hourly activities, for the busiest time. */
  hourlyActivities?: readonly unknown[];
}

/** The selection result of the rows the data API sent. */
export function catalogueEeResponse(input: CatalogueActivitiesRows): EeResponse {
  const rows = countedRows(input.activities, input.type);
  const body: EeResponse = {
    geo_type: input.geoType,
    date: input.date,
    type: input.type,
    rows,
    summary: summarise(rows),
    grow: null,
    distributions: distributionsOf(input.distributions, input.type, rows),
    people_is_sum_of_areas: new Set(rows.map((r) => r.geo_id)).size > 1,
  };
  const busiest = busiestTime(input.type === 'hourly' ? input.activities : input.hourlyActivities ?? []);
  if (busiest) body.busiest_time = busiest;
  if (input.type === 'hourly' && input.day) {
    // The day's figures for the same selection, as a daily request gives them.
    const dayRows = countedRows(input.day.activities, 'daily');
    const daySummary = summarise(dayRows)[0];
    body.day_arrivals = dayArrivalsOf(daySummary?.arrivals ?? null, distributionsOf(input.day.distributions, 'daily', dayRows));
  }
  return body;
}
